import { getProAppOrigin, hardNavigateOnce } from "./app-origins";
import { prefetchProductAppUrl, prefetchProApp } from "./prefetch-product-app";
import { markSessionWarmed } from "./session-warm";

const EXTERNAL_REDIRECT_STORAGE_KEY = "lion_pro_pending_external_redirect";
/** Survive email-verify in a new tab (sessionStorage is per-tab). */
const EXTERNAL_REDIRECT_TTL_MS = 24 * 60 * 60 * 1000;

type StoredRedirect = { url: string; storedAt: number };

export type ExternalRedirectApp = "pro" | "certification";

/**
 * Product apps that share the auth cookie (see session-cookie.ts) and can be
 * returned to after login/registration on account-web. Extend this list as
 * more apps are split out. The `app` key is also used to pick a per-app login
 * background (see auth-presentation.ts).
 */
const EXTERNAL_APPS: { app: ExternalRedirectApp; patterns: RegExp[] }[] = [
  {
    app: "pro",
    patterns: [
      /^https:\/\/pro(-dev)?\.lionenergy\.com(\/|$)/,
      /^http:\/\/(localhost|127\.0\.0\.1):5173(\/|$)/,
      /^http:\/\/pro\.localhost:5173(\/|$)/,
    ],
  },
  {
    app: "certification",
    patterns: [
      /^https:\/\/certification(-dev)?\.lionenergy\.com(\/|$)/,
      // Prod preview host until cutover (new SPA on *-legacy).
      /^https:\/\/certification-legacy\.lionenergy\.com(\/|$)/,
      // Local dev: certification on :5181 (pro :5173, account :5174).
      /^http:\/\/(localhost|127\.0\.0\.1):5181(\/|$)/,
      /^http:\/\/certification\.localhost:5181(\/|$)/,
    ],
  },
];

function isAbsoluteUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

/**
 * Local Google SSO must round-trip through http://localhost:5174 (Google's
 * only non-HTTPS exception). Rewrite *.localhost product return URLs to the
 * matching localhost port so the host-only auth cookie remains visible.
 */
function normalizeLocalReturnUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === "pro.localhost") {
      parsed.hostname = "localhost";
      parsed.port = "5173";
      return parsed.toString();
    }
    if (parsed.hostname === "certification.localhost") {
      parsed.hostname = "localhost";
      parsed.port = "5181";
      return parsed.toString();
    }
    if (parsed.hostname === "account.localhost") {
      parsed.hostname = "localhost";
      parsed.port = "5174";
      return parsed.toString();
    }
  } catch {
    // ignore
  }
  return url;
}

function matchExternalApp(url: string): ExternalRedirectApp | null {
  return EXTERNAL_APPS.find((entry) => entry.patterns.some((p) => p.test(url)))?.app ?? null;
}

export function isAllowedExternalRedirect(url: string): boolean {
  return matchExternalApp(url) !== null;
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Firefox can throw DOMException ("The operation is insecure") when storage
    // is blocked; auth still works — we just lose the pending return URL.
  }
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function safeLocalSet(value: StoredRedirect): void {
  try {
    localStorage.setItem(EXTERNAL_REDIRECT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function safeLocalGet(): StoredRedirect | null {
  try {
    const raw = localStorage.getItem(EXTERNAL_REDIRECT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRedirect;
    if (typeof parsed?.url !== "string" || typeof parsed?.storedAt !== "number") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function safeLocalRemove(): void {
  try {
    localStorage.removeItem(EXTERNAL_REDIRECT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function persistPendingRedirect(url: string): void {
  const normalized = normalizeLocalReturnUrl(url);
  safeSessionSet(EXTERNAL_REDIRECT_STORAGE_KEY, normalized);
  safeLocalSet({ url: normalized, storedAt: Date.now() });
}

function clearPendingRedirect(): void {
  safeSessionRemove(EXTERNAL_REDIRECT_STORAGE_KEY);
  safeLocalRemove();
}

/**
 * Called on /login and /register entry (via requireGuest()) when the `redirect`
 * search param points at another Lion Pro app. Stored in sessionStorage (same
 * tab) and localStorage (email-verify / new tab) rather than threaded through
 * every onboarding URL.
 */
export function capturePendingExternalRedirect(redirect: string | undefined): void {
  if (typeof window === "undefined" || !redirect) return;
  if (isAbsoluteUrl(redirect) && isAllowedExternalRedirect(redirect)) {
    persistPendingRedirect(redirect);
  }
}

function getPendingExternalRedirect(): string | null {
  if (typeof window === "undefined") return null;

  const fromSession = safeSessionGet(EXTERNAL_REDIRECT_STORAGE_KEY);
  if (fromSession && isAllowedExternalRedirect(fromSession)) return fromSession;

  const stored = safeLocalGet();
  if (!stored) return null;
  if (Date.now() - stored.storedAt > EXTERNAL_REDIRECT_TTL_MS) {
    safeLocalRemove();
    return null;
  }
  if (!isAllowedExternalRedirect(stored.url)) {
    safeLocalRemove();
    return null;
  }
  return stored.url;
}

/**
 * Which app (if any) the current login/register visit is on behalf of —
 * used purely for presentation (e.g. picking a background image), not auth.
 */
export function getPendingExternalRedirectApp(): ExternalRedirectApp | null {
  const target = getPendingExternalRedirect();
  return target ? matchExternalApp(target) : null;
}

/**
 * Warm the post-auth destination (pending redirect or pro home) while the user
 * is still on account-web. Call from login mount, Google click, and OAuth callback.
 */
export function prefetchReturnDestination(): void {
  const target = getPendingExternalRedirect();
  if (target) {
    prefetchProductAppUrl(target);
    return;
  }
  prefetchProApp();
}

/**
 * If a pending external redirect exists, navigates there. No tokens to hand
 * off — the auth cookie is already shared across *.lionenergy.com (see
 * session-cookie.ts), so it's simply valid on the target origin the moment
 * the browser arrives. Returns true if a redirect happened, so the caller
 * skips its own internal navigation.
 */
export function returnToExternalAppIfPending(): boolean {
  const target = getPendingExternalRedirect();
  if (!target) return false;

  clearPendingRedirect();
  hardNavigateOnce(normalizeLocalReturnUrl(target));
  return true;
}

/**
 * After successful auth/onboarding on account-web: return to the pending
 * product app if one was captured, otherwise hard-navigate to pro-web home.
 * Always returns true when a navigation was kicked off.
 */
export function returnAfterAuth(): boolean {
  // Ensure destination apps skip verify-token and can use the bootstrap cookie
  // written by completeSession (refreshes TTL if already marked).
  markSessionWarmed();
  if (returnToExternalAppIfPending()) return true;
  if (typeof window === "undefined") return false;
  hardNavigateOnce(`${getProAppOrigin()}/`);
  return true;
}
